// In-memory queue + sessionStorage backup + delivery scheduler.
//
// Public entrypoint is `enqueue(event)`. Delivery is driven by a background
// timer plus visibility/unload hooks. This module holds NO auth/role logic —
// callers gate emission before calling `enqueue` (see `track.ts`).
//
// Correctness rules:
//   - `enqueue` NEVER throws.
//   - Overflow drops the OLDEST events so newer signals survive back-pressure.
//   - sessionStorage backup is a best-effort mirror — cleared on successful
//     flush so a hard reload never re-sends already-delivered events.
//   - Delivery on `visibilitychange=hidden` and `beforeunload` uses
//     `fetch(..., { keepalive: true })` so the request survives page unload
//     AND preserves the Authorization header (unlike sendBeacon).

import api, { getAccessToken } from '@/shared/api/client'
import { ANALYTICS_CONFIG } from './config'
import type { AnalyticsPrimitive, AnalyticsProps } from './config'

export interface QueuedEvent {
  event_time: string
  area: string
  feature: string
  sub_feature?: string
  page_path?: string
  props?: Record<string, AnalyticsPrimitive>
}

let queue: QueuedEvent[] = []
let timer: ReturnType<typeof setInterval> | null = null
let warned = false
let installed = false

function loadFromSession(): void {
  try {
    const raw = window.sessionStorage.getItem(ANALYTICS_CONFIG.SESSION_STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      queue = parsed.slice(-ANALYTICS_CONFIG.QUEUE_MAX)
    }
  } catch {
    /* ignored — sessionStorage might be disabled */
  }
}

function persistToSession(): void {
  try {
    if (queue.length === 0) {
      window.sessionStorage.removeItem(ANALYTICS_CONFIG.SESSION_STORAGE_KEY)
      return
    }
    window.sessionStorage.setItem(
      ANALYTICS_CONFIG.SESSION_STORAGE_KEY,
      JSON.stringify(queue),
    )
  } catch {
    /* ignored — quota or privacy mode */
  }
}

/** Merge in-memory queue and sessionStorage state, keeping newest events. */
function enqueue(event: QueuedEvent): void {
  try {
    queue.push(event)
    if (queue.length > ANALYTICS_CONFIG.QUEUE_MAX) {
      queue = queue.slice(-ANALYTICS_CONFIG.QUEUE_MAX)
    }
    persistToSession()
  } catch {
    /* fatal-swallow — analytics must NEVER interrupt the user path */
  }
}

async function postBatchViaAxios(batch: QueuedEvent[]): Promise<boolean> {
  try {
    const res = await api.post(ANALYTICS_CONFIG.ENDPOINT, { events: batch })
    return res.status >= 200 && res.status < 300
  } catch (err) {
    if (!warned) {
      warned = true
      // eslint-disable-next-line no-console
      console.warn('[analytics] delivery failed', (err as Error)?.message)
    }
    return false
  }
}

/**
 * Deliver via `fetch(..., { keepalive: true })` so the request survives page
 * unload. Preserves the JWT bearer header (unlike sendBeacon). Called on
 * visibilitychange=hidden / beforeunload — no retry, best-effort.
 */
function postBatchViaKeepalive(batch: QueuedEvent[]): void {
  try {
    const token = getAccessToken()
    if (!token) return
    // Reconstruct the URL by matching axios's baseURL rule.
    const baseURL =
      (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api/v1/app'
    const url = `${baseURL}${ANALYTICS_CONFIG.ENDPOINT}`
    fetch(url, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ events: batch }),
    }).catch(() => undefined)
  } catch {
    /* swallow */
  }
}

/**
 * Send everything currently queued in ordered chunks of ≤ BATCH_MAX.
 * `mode='live'` uses axios (retries handled by our interceptor).
 * `mode='keepalive'` uses fetch+keepalive for unload / visibility-hidden.
 * On success, clears the sent events from queue + sessionStorage.
 *
 * Keepalive under `navigator.onLine === false` (offline tab-hide) is a
 * no-op: we cannot retry after the fetch fails and cannot await it either,
 * so we leave the queue intact. Next `live` flush after reconnection
 * drains it.
 */
export async function flush(mode: 'live' | 'keepalive' = 'live'): Promise<void> {
  if (queue.length === 0) return

  if (mode === 'keepalive' && typeof navigator !== 'undefined' && navigator.onLine === false) {
    // Offline visibility-hidden / beforeunload → keepalive fetch would
    // fail immediately with the queue already drained. Preserve events.
    return
  }

  const pending = queue
  // Optimistically clear so new events during flight don't collide with the
  // just-flushed slice. On failure we prepend back.
  queue = []
  persistToSession()

  const chunks: QueuedEvent[][] = []
  for (let i = 0; i < pending.length; i += ANALYTICS_CONFIG.BATCH_MAX) {
    chunks.push(pending.slice(i, i + ANALYTICS_CONFIG.BATCH_MAX))
  }

  if (mode === 'keepalive') {
    // Fire-and-forget every chunk; ignore results.
    for (const chunk of chunks) postBatchViaKeepalive(chunk)
    return
  }

  const failed: QueuedEvent[] = []
  for (const chunk of chunks) {
    const ok = await postBatchViaAxios(chunk)
    if (!ok) failed.push(...chunk)
  }
  if (failed.length > 0) {
    // Re-queue at the front, respecting the cap.
    queue = [...failed, ...queue].slice(-ANALYTICS_CONFIG.QUEUE_MAX)
    persistToSession()
  }
}

/**
 * Install background timer + visibility/unload hooks. Safe to call more than
 * once — subsequent calls are no-ops. Called by `PageViewTracker` on mount.
 */
export function installScheduler(): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  loadFromSession()

  timer = setInterval(() => {
    void flush('live')
  }, ANALYTICS_CONFIG.FLUSH_INTERVAL_MS)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush('keepalive')
  })
  window.addEventListener('beforeunload', () => flush('keepalive'))
}

/**
 * Called from the logout handler in `use-auth.ts` right before the JWT is
 * cleared. Two rules apply here that don't apply elsewhere:
 *
 *   1. Best-effort delivery when online — keepalive fetch survives the
 *      imminent nav to `/auth/login`.
 *   2. ALWAYS drop the queue + sessionStorage after, whether online or not.
 *      Otherwise a user who logs out while offline would leave their
 *      events queued for the NEXT user of the same tab, and analytics
 *      would attribute them under the wrong identity. Data loss on
 *      offline logout is preferable to cross-user attribution.
 */
export function flushOnLogout(): void {
  try {
    if (queue.length > 0 && typeof navigator !== 'undefined' && navigator.onLine !== false) {
      const chunks: QueuedEvent[][] = []
      for (let i = 0; i < queue.length; i += ANALYTICS_CONFIG.BATCH_MAX) {
        chunks.push(queue.slice(i, i + ANALYTICS_CONFIG.BATCH_MAX))
      }
      for (const chunk of chunks) postBatchViaKeepalive(chunk)
    }
  } finally {
    queue = []
    try {
      window.sessionStorage.removeItem(ANALYTICS_CONFIG.SESSION_STORAGE_KEY)
    } catch {
      /* swallow */
    }
  }
}

/** Test-only reset. */
export function _resetAnalyticsQueueForTests(): void {
  queue = []
  installed = false
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  try {
    window.sessionStorage.removeItem(ANALYTICS_CONFIG.SESSION_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

export const _queueInternalForTests = {
  peek: () => [...queue],
  size: () => queue.length,
}

export { enqueue }
export type { AnalyticsProps }
